import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Trash2, Upload, AlertTriangle } from 'lucide-react'
import { supabase, isDemoMode, uploadFile } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'
import SavedIndicator from '../components/SavedIndicator'

const FIELDS = [
  ['sku',          'SKU',                       'text'],
  ['name',         'Product name',              'text'],
  ['unit',         'Unit (e.g. each, box, kg)', 'text'],
  ['pack_size',    'Pack size (units per box)', 'number'],
  ['price',        'Price (£)',                 'number'],
  ['sage_code',    'Sage product code',         'text'],
  ['description',  'Description',               'textarea'],
]

export default function ProductDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form, setForm] = useState(null)
  const [cardUsage, setCardUsage] = useState([])
  const [groups, setGroups] = useState([])
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(0)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [uploading, setUploading] = useState(false)

  const load = async () => {
    if (isDemoMode) { setForm({}); return }
    const [prodRes, usageRes, groupsRes] = await Promise.all([
      supabase.from('products').select('*').eq('id', id).maybeSingle(),
      supabase.from('card_products').select('id, card_id, cards(id, name, company_id, companies(id, name))').eq('product_id', id),
      supabase.from('client_groups').select('id, name').is('archived_at', null).order('name'),
    ])
    if (prodRes.data) setForm(prodRes.data)
    setCardUsage(usageRes.data || [])
    setGroups(groupsRes.data || [])
  }
  useEffect(() => { load() }, [id])

  const set = (key, value) => setForm({ ...form, [key]: value })

  const onSaveClick = () => {
    if (cardUsage.length > 0) setConfirmOpen(true)
    else doSave()
  }

  const doSave = async () => {
    if (isDemoMode) { setConfirmOpen(false); return }
    setSaving(true)
    const { id: _, created_at, updated_at, ...payload } = form
    if (payload.price === '' || payload.price == null) payload.price = null
    else payload.price = Number(payload.price)
    if (payload.pack_size === '' || payload.pack_size == null) payload.pack_size = null
    else payload.pack_size = parseInt(payload.pack_size, 10) || null
    const { error } = await supabase.from('products').update(payload).eq('id', id)
    setSaving(false)
    setConfirmOpen(false)
    if (error) return alert(error.message)
    setSavedAt(Date.now())
  }

  const archive = async () => {
    if (isDemoMode) return
    if (!confirm('Archive this product? It stays on past orders but is hidden from lists.')) return
    await supabase.from('products').update({ archived_at: new Date().toISOString() }).eq('id', id)
    navigate('/products')
  }

  const onImage = async (e) => {
    const file = e.target.files?.[0]
    if (!file || isDemoMode) return
    setUploading(true)
    try {
      const { url } = await uploadFile(file, 'product-images')
      await supabase.from('products').update({ image_url: url }).eq('id', id)
      setForm(f => ({ ...f, image_url: url }))
    } catch (err) {
      alert(err.message)
    } finally {
      setUploading(false)
    }
  }

  if (!form) return <div className="card"><p className="text-sm text-gray-500">Loading…</p></div>

  const cardCount = cardUsage.length
  const companyCount = new Set(cardUsage.map(u => u.cards?.company_id).filter(Boolean)).size

  return (
    <div className="space-y-4">
      <DemoNotice />

      <Link to="/products" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> All products
      </Link>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Product details</h3>
          <button onClick={archive} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1">
            <Trash2 className="w-4 h-4" /> Archive
          </button>
        </div>

        <div className="flex gap-6 mb-4">
          <div>
            {form.image_url
              ? <img src={form.image_url} alt="" className="w-32 h-32 rounded-lg object-cover bg-gray-100" />
              : <div className="w-32 h-32 rounded-lg bg-brand-50 border-2 border-dashed border-brand-light" />
            }
            <label className="btn-secondary mt-2 text-sm cursor-pointer justify-center">
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading…' : 'Image'}
              <input type="file" accept="image/*" onChange={onImage} className="hidden" />
            </label>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map(([key, label, type]) => (
              <label key={key} className={type === 'textarea' ? 'sm:col-span-2' : ''}>
                <span className="text-xs text-gray-500 block mb-1">{label}</span>
                {type === 'textarea'
                  ? <textarea value={form[key] || ''} onChange={e => set(key, e.target.value)} className="input-field" rows={3} />
                  : <input type={type} step={type === 'number' ? '0.01' : undefined} value={form[key] ?? ''} onChange={e => set(key, e.target.value)} className="input-field" />
                }
              </label>
            ))}
            <label className="sm:col-span-2">
              <span className="text-xs text-gray-500 block mb-1">Owner (which client group is this product for?)</span>
              <select
                value={form.owner_client_group_id || ''}
                onChange={e => set('owner_client_group_id', e.target.value || null)}
                className="input-field"
              >
                <option value="">— Master (any client) —</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <span className="block text-[11px] text-gray-400 mt-1">
                Groups the product under this owner on the Products page. Doesn't change who can order it — set that on <Link to="/admin/product-visibility" className="underline text-brand-dark">Product Visibility</Link>.
              </span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={onSaveClick} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <SavedIndicator savedAt={savedAt} />
        </div>
      </div>

      {cardCount > 0 && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-2">Used on {cardCount} card{cardCount === 1 ? '' : 's'} across {companyCount} compan{companyCount === 1 ? 'y' : 'ies'}</h3>
          <ul className="divide-y divide-gray-100">
            {cardUsage.map(u => (
              <li key={u.id} className="py-2 flex items-center gap-2 text-sm">
                <span className="text-gray-800 font-medium">{u.cards?.companies?.name}</span>
                <span className="text-gray-400">·</span>
                <Link to={`/cards/${u.card_id}`} className="text-brand-dark hover:underline">{u.cards?.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Propagation confirm dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirmOpen(false)}>
          <div className="card max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-800">Apply changes everywhere?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  This product is used on <strong>{cardCount} card{cardCount === 1 ? '' : 's'}</strong>{' '}
                  across <strong>{companyCount} compan{companyCount === 1 ? 'y' : 'ies'}</strong>.
                  Your edit will update the product everywhere it appears, except where a card has explicitly overridden a field.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setConfirmOpen(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={doSave} disabled={saving} className="btn-primary text-sm">
                {saving ? 'Saving…' : 'Apply to all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
